import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import PageHero from '../../components/public/PageHero';
import CnibMap from '../../components/CnibMap';
import api from '../../lib/api';
import { OPERATIONS, OPERATION_LABELS } from '../../lib/constants';

export default function Mapa() {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operation, setOperation] = useState('');

  useEffect(() => {
    api
      .get('/properties/map/points')
      .then(({ data }) => setPoints(data))
      .catch(() => setPoints([]))
      .finally(() => setLoading(false));
  }, []);

  const markers = useMemo(() => {
    return points
      .filter((p) => !operation || p.operation === operation)
      .map((p) => {
        const op = OPERATIONS.find((o) => o.value === p.operation);
        return {
          id: p.property_id,
          lat: p.lat,
          lng: p.lng,
          color: op?.color || '#F97316',
          popupHtml: `
            <div style="font-family:DM Sans,sans-serif;max-width:220px">
              ${p.images?.[0] ? `<img src="${p.images[0]}" style="width:100%;height:90px;object-fit:cover;border-radius:10px" />` : ''}
              <p style="font-weight:800;margin:8px 0 2px;font-size:13px;color:#0F172A">${p.title}</p>
              <p style="margin:0;font-size:11px;color:#64748B">${p.zone ? p.zone + ', ' : ''}${p.city}</p>
              <p style="margin:6px 0;font-size:14px;font-weight:800;color:#0F4C81">US$ ${Number(p.price_usd).toLocaleString('es-BO')}</p>
              <a href="/propiedades/${p.property_id}" style="display:inline-block;background:#F97316;color:white;font-weight:700;font-size:11px;padding:6px 14px;border-radius:8px;text-decoration:none">Ver ficha</a>
            </div>`,
        };
      });
  }, [points, operation]);

  return (
    <div>
      <PageHero
        eyebrow="Mapa colaborativo"
        title="Toda la red inmobiliaria, en un mapa"
        description="Explora las propiedades publicadas por socios CNIB en los 9 departamentos de Bolivia."
      />

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center gap-2" data-testid="map-filters">
          <button
            onClick={() => setOperation('')}
            className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${!operation ? 'bg-cnib-navy text-white' : 'bg-white text-slate-500 hover:text-slate-800'}`}
            data-testid="map-filter-all"
          >
            Todas ({points.length})
          </button>
          {OPERATIONS.map((op) => (
            <button
              key={op.value}
              onClick={() => setOperation(op.value)}
              className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${operation === op.value ? 'text-white' : 'bg-white text-slate-500 hover:text-slate-800'}`}
              style={operation === op.value ? { background: op.color } : {}}
              data-testid={`map-filter-${op.value}`}
            >
              {op.label} ({points.filter((p) => p.operation === op.value).length})
            </button>
          ))}
        </div>

        <div className="relative mt-6 overflow-hidden rounded-3xl border border-slate-200 shadow-xl" data-testid="map-container">
          {loading ? (
            <div className="flex h-[70vh] items-center justify-center bg-slate-100">
              <Loader2 className="h-10 w-10 animate-spin text-cnib-blue" />
            </div>
          ) : (
            <CnibMap center={[-63.6, -17.2]} zoom={5.2} markers={markers} className="h-[70vh] w-full" />
          )}
          <div className="absolute bottom-4 left-4 flex flex-wrap gap-2 rounded-2xl bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
            {OPERATIONS.map((op) => (
              <span key={op.value} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: op.color }} />
                {OPERATION_LABELS[op.value]}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
